/**
 * Utility functions for the VariantChildrenGrid widget.
 *
 * These functions support filtering, searching, and data extraction
 * for variant product grids. They are designed to be pure functions
 * with no side effects, making them easy to test and reuse.
 */

import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { INCLUDES_TEXT } from "@thkStore/Constants/ProductPropertiesKeys";
import { CustomProperty } from "@thkStore/Types/CustomPropertiesByParentModel";
import { VariantFilters } from "@thkStore/Types/VariantChildrenGridModel";

// ============================================================================
// ATTRIBUTE VALUE EXTRACTION
// Functions for extracting attribute and trait values from products
// ============================================================================

/**
 * Retrieves the first attribute value for a given attribute type from a product.
 *
 * This function is used during filtering to get a product's value for a specific
 * attribute type (e.g., "Color", "Size"). In the variant model, each attribute type
 * typically has only one value per variant product.
 *
 * @param product - The product to extract the attribute value from
 * @param attributeTypeId - The ID of the attribute type to look up
 * @returns The attribute value string, or undefined if not found
 *
 * @example
 * // Get the "Color" attribute value for a variant
 * const color = getAttributeValueIdForProduct(variant, "color-attr-id");
 * // Returns: "Red" or undefined
 */
export const getAttributeValueIdForProduct = (product: ProductModel, attributeTypeId: string): string | undefined => {
    // Find the attribute type on this product that matches the requested ID
    const attributeType = product.attributeTypes?.find(at => at.id === attributeTypeId);

    // Return undefined if attribute type not found or has no values
    if (!attributeType?.attributeValues?.length) {
        return undefined;
    }

    // Return the first (and typically only) attribute value
    // Variants usually have exactly one value per attribute type
    return attributeType.attributeValues[0].value;
};

/**
 * Extracts all unique attribute types from a collection of variant products.
 *
 * This function aggregates attribute types across all variants to build the
 * complete set of filterable attributes. It handles the case where different
 * variants may have different attribute types defined.
 *
 * The result is sorted by sortOrder to ensure consistent display ordering
 * in the filter UI.
 *
 * @param variantChildren - Array of variant products to extract attribute types from
 * @returns Array of unique AttributeTypeModel objects, sorted by sortOrder
 *
 * @example
 * // Given variants with Color and Size attributes
 * const attributeTypes = getUniqueAttributeTypes(variants);
 * // Returns: [{ id: "color", name: "Color", sortOrder: 1 }, { id: "size", name: "Size", sortOrder: 2 }]
 */
export const getUniqueAttributeTypes = (variantChildren: ProductModel[]): AttributeTypeModel[] => {
    // Use a Map to deduplicate attribute types by ID
    // Map preserves insertion order, but we'll sort explicitly for consistency
    const attributeTypeMap = new Map<string, AttributeTypeModel>();

    // Iterate through all variants and collect their attribute types
    variantChildren.forEach(variant => {
        variant.attributeTypes?.forEach(attrType => {
            // Only add if we haven't seen this attribute type yet
            // First occurrence wins (takes the attribute type definition from first variant that has it)
            if (!attributeTypeMap.has(attrType.id)) {
                // Create a shallow copy to avoid mutating the original
                attributeTypeMap.set(attrType.id, { ...attrType });
            }
        });
    });

    // Convert to array and sort by sortOrder for consistent UI ordering
    return Array.from(attributeTypeMap.values()).sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Retrieves the display value for a style trait from a variant product.
 *
 * Style traits (like "Color" or "Size" for configurable products) are stored
 * differently than regular attributes. This function extracts the trait value
 * from the childTraitValues collection.
 *
 * Prioritizes valueDisplay over value for user-friendly display
 * (e.g., "Extra Large" instead of "XL").
 *
 * @param product - The variant product to extract the trait value from
 * @param traitId - The style trait ID to look up
 * @returns The trait's display value, falling back to raw value, or undefined if not found
 *
 * @example
 * // Get the "Size" trait value for a variant
 * const size = getVariantTraitValueForProduct(variant, "size-trait-id");
 * // Returns: "Extra Large" (valueDisplay) or "XL" (value) or undefined
 */
export const getVariantTraitValueForProduct = (product: ProductModel, traitId: string): string | undefined => {
    // Find the child trait value matching the requested trait ID
    const traitValue = product.childTraitValues?.find(ctv => ctv.styleTraitId === traitId);

    // Return valueDisplay if available (user-friendly), otherwise fall back to raw value
    return traitValue?.valueDisplay || traitValue?.value;
};

// ============================================================================
// SEARCH AND FILTERING
// Functions for filtering variants based on search terms
// ============================================================================

/**
 * Determines if a variant product matches the active search-within filters.
 *
 * Search-within allows users to type text to filter down results further.
 * This function checks if the variant matches ANY of the search terms (OR logic).
 *
 * SEARCH BEHAVIOR:
 * - Case-insensitive matching
 * - Searches across multiple fields: product title, product number, and all attribute values
 * - Multiple search terms use OR logic (variant matches if it contains ANY term)
 * - Empty search terms array means no filtering (all variants pass)
 *
 * FIELDS SEARCHED:
 * 1. Product title (e.g., "Red Widget - Large")
 * 2. Product number/SKU (e.g., "WGT-RED-LG")
 * 3. All attribute values (e.g., "Red", "Large", "Cotton")
 *
 * @param filters - The current filter state containing searchWithin terms
 * @param variant - The variant product to test against search terms
 * @returns true if variant matches any search term (or if no search terms), false otherwise
 *
 * @example
 * // Check if a variant matches search filters
 * const filters = { searchWithin: ["red", "large"] };
 * const matches = filterSearchTerms(filters, variant);
 * // Returns true if variant title/number/attributes contain "red" OR "large"
 */
export const filterSearchTerms = (filters: VariantFilters, variant: ProductModel): boolean => {
    // No search terms means no filtering - all variants pass
    if (!filters.searchWithin || filters.searchWithin.length === 0) {
        return true;
    }

    // Check if variant matches ANY of the search terms (OR logic)
    return filters.searchWithin.some(searchTerm => {
        // Normalize search term for case-insensitive comparison
        const lowerSearchTerm = searchTerm.toLowerCase();

        // Check product title (e.g., "Red Widget - Large")
        const titleMatch = variant.productTitle?.toLowerCase().includes(lowerSearchTerm);
        if (titleMatch) {
            return true;
        }

        // Check product number/SKU (e.g., "WGT-RED-LG")
        const numberMatch = variant.productNumber?.toLowerCase().includes(lowerSearchTerm);
        if (numberMatch) {
            return true;
        }

        // Check all attribute values across all attribute types
        // This allows searching by attribute values like "Red", "Cotton", etc.
        const attributeMatch = variant.attributeTypes?.some(attrType =>
            attrType.attributeValues?.some(attrValue =>
                attrValue.value?.toLowerCase().includes(lowerSearchTerm)
            )
        );

        return attributeMatch ?? false;
    });
};

// ============================================================================
// CONFIGURATION PARSING
// Functions for parsing widget configuration values
// ============================================================================

/**
 * Parses a comma-separated string of page size options into an array of numbers.
 *
 * This function is used to convert the widget's page size configuration
 * (stored as a string in the CMS) into a usable array of numbers for
 * the pagination dropdown.
 *
 * PARSING BEHAVIOR:
 * - Splits by comma
 * - Trims whitespace from each value
 * - Parses as base-10 integers
 * - Filters out invalid/NaN values (graceful handling of malformed input)
 *
 * @param optionsStr - Comma-separated string of page sizes (e.g., "10, 25, 50, 100")
 * @returns Array of valid page size numbers
 *
 * @example
 * parsePageSizeOptions("10, 25, 50, 100")
 * // Returns: [10, 25, 50, 100]
 *
 * @example
 * // Handles malformed input gracefully
 * parsePageSizeOptions("10, invalid, 50")
 * // Returns: [10, 50]
 */
export const parsePageSizeOptions = (optionsStr: string): number[] => {
    return optionsStr
        // Split the string into individual values
        .split(",")
        // Parse each value as an integer (base 10), trimming whitespace
        .map(s => parseInt(s.trim(), 10))
        // Filter out any values that couldn't be parsed (NaN)
        .filter(n => !Number.isNaN(n));
};

/**
 * Derives the "includes" label text from a parent product's custom properties and title.
 *
 * Logic:
 * 1. If the product has an explicit INCLUDES_TEXT custom property, use it.
 * 2. Otherwise infer from the product title: "rail" products include blocks and vice versa.
 * 3. Falls back to empty string if neither condition is met.
 *
 * @param product - The parent product model
 * @returns The includes label text (e.g., "block", "rail", or a custom string)
 */
export const getIncludesText = (product: ProductModel | undefined): string => {    
    const includesText = product?.properties?.[INCLUDES_TEXT];
    if (includesText) {
        return includesText;
    }

    const title = product?.productTitle?.toLowerCase();
    if (title?.includes("rail")) {
        return translate("block");
    }
    if (title?.includes("block")) {
        return translate("rail");
    }

    return "";
};

const PROPERTY_HIDE_TRAIT = "hideTrait";

export const hideTrait = (customProperties: CustomProperty[]) =>
{
    if (!customProperties || customProperties.length === 0) {
        return false;
    }
    return customProperties.some(prop => prop.name === PROPERTY_HIDE_TRAIT && prop.value === "true");
};